import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

export interface ErrorInfo {
  message: string;
  code?: string;
  statusCode?: number;
  timestamp: Date;
  context?: Record<string, any>;
}

export interface UseErrorHandlerOptions {
  onError?: (error: ErrorInfo) => void;
  showNotification?: boolean;
  logToConsole?: boolean;
}

/**
 * Hook for centralized error handling across the application
 */
export function useErrorHandler(options: UseErrorHandlerOptions = {}) {
  const { onError, showNotification = true, logToConsole = true } = options;

  const [errors, setErrors] = useState<ErrorInfo[]>([]);
  const queryClient = useQueryClient();

  const handleError = useCallback(
    (error: any, context?: Record<string, any>) => {
      const errorInfo: ErrorInfo = {
        message: getErrorMessage(error),
        code: error?.code || error?.response?.data?.code,
        statusCode: error?.response?.status,
        timestamp: new Date(),
        context,
      };

      // Add to error list
      setErrors(prev => [errorInfo, ...prev.slice(0, 9)]); // Keep last 10 errors

      // Log to console if enabled
      if (logToConsole) {
        console.error('Application Error:', errorInfo, error);
      }

      // Call custom error handler
      onError?.(errorInfo);

      // Handle specific error types
      handleSpecificErrors(error, errorInfo);

      return errorInfo;
    },
    [onError, logToConsole]
  );

  const handleSpecificErrors = (error: any, errorInfo: ErrorInfo) => {
    const statusCode = error?.response?.status;

    switch (statusCode) {
      case 401:
        // Unauthorized - clear auth and redirect to login
        queryClient.clear();
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        break;

      case 403:
        // Forbidden - show access denied message
        if (showNotification) {
          showErrorNotification(
            'Access denied. You do not have permission to perform this action.'
          );
        }
        break;

      case 404:
        // Not found - might be a routing issue
        if (showNotification) {
          showErrorNotification('The requested resource was not found.');
        }
        break;

      case 429:
        // Rate limited - show rate limit message
        if (showNotification) {
          showErrorNotification(
            'Too many requests. Please wait a moment before trying again.'
          );
        }
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors - show generic server error message
        if (showNotification) {
          showErrorNotification('Server error. Please try again later.');
        }
        break;

      default:
        // Network or other errors
        if (!error?.response && showNotification) {
          showErrorNotification(
            'Network error. Please check your connection and try again.'
          );
        } else if (showNotification) {
          showErrorNotification(errorInfo.message);
        }
    }
  };

  const showErrorNotification = (message: string) => {
    // This would integrate with your notification system
    // For now, we'll use a simple alert or could integrate with a toast library
    console.error('Error Notification:', message);

    // You could integrate with libraries like react-hot-toast, react-toastify, etc.
    // toast.error(message);
  };

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const clearError = useCallback((index: number) => {
    setErrors(prev => prev.filter((_, i) => i !== index));
  }, []);

  const retryLastFailedQuery = useCallback(() => {
    // Retry the last failed query
    queryClient.refetchQueries({
      type: 'all',
      stale: true,
    });
  }, [queryClient]);

  return {
    errors,
    handleError,
    clearErrors,
    clearError,
    retryLastFailedQuery,
    hasErrors: errors.length > 0,
    latestError: errors[0] || null,
  };
}

/**
 * Extract a user-friendly error message from various error types
 */
function getErrorMessage(error: any): string {
  // API response error
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  // Axios error
  if (error?.message) {
    return error.message;
  }

  // String error
  if (typeof error === 'string') {
    return error;
  }

  // Error object
  if (error instanceof Error) {
    return error.message;
  }

  // Fallback
  return 'An unexpected error occurred';
}

/**
 * Hook for handling React Query errors specifically
 */
export function useQueryErrorHandler() {
  const { handleError } = useErrorHandler();

  return useCallback(
    (error: any, query: any) => {
      const context = {
        queryKey: query?.queryKey,
        queryHash: query?.queryHash,
        state: query?.state,
      };

      return handleError(error, context);
    },
    [handleError]
  );
}

/**
 * Hook for handling mutation errors specifically
 */
export function useMutationErrorHandler() {
  const { handleError } = useErrorHandler();

  return useCallback(
    (error: any, variables: any, context: any) => {
      const errorContext = {
        mutationVariables: variables,
        mutationContext: context,
      };

      return handleError(error, errorContext);
    },
    [handleError]
  );
}
